import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { useFeature } from '../featureFlags/FeatureGate';
import { useHasAnyPermission } from '../PermissionGate';
import { toErrorMessage } from './orderActionsApi';
import {
  FEATURE_INTAKE_CANCELLATION,
  MIN_CANCELLATION_REASON_LENGTH,
  cancelOrder,
  cancellable,
  fetchIntakeCancellation,
  markDeletedInTms,
  type IntakeCancellationView,
} from './intakeCancellationApi';

/**
 * Anulowanie zlecenia przez CS i Administrację (prace 25.10.2026,
 * S-25.10-05, przełącznik `intakeOrderCancellation`; decyzja użytkownika
 * 24.10.2026, A4 z 21.10.2026: „w aplikacji zapis decyzji, zmianę/anulację
 * w TMS robi człowiek”).
 *
 * Na karcie zlecenia i prezlecenia: przycisk „Anuluj zlecenie” (CS
 * i Administracja), a po decyzji — stan „Anulowane w aplikacji — do
 * usunięcia w TMS” z „Usunięte w TMS” (oświadczenie). Stan widzi każdy,
 * przyciski tylko z uprawnieniem działu. Do TMS nic nie wysyłamy.
 * Wyłączony przełącznik — nic, jak dotąd.
 */

export const CANCEL_DIALOG_TEXT =
  'Zlecenie przejdzie do stanu „Nieaktualne”: obsadzony pojazd i warianty zostaną zwolnione, ' +
  'a oferty na giełdach zejdą same. Działy FTL i Trade dostaną powiadomienie. ' +
  'Do TMS nic nie wysyłamy — zlecenie w TMS usuń sam, a potem oznacz „Usunięte w TMS”.';

export const DELETED_DIALOG_TEXT =
  'Potwierdzasz, że zlecenie zostało usunięte w TMS. To Twoje oświadczenie — aplikacja ' +
  'nie sprawdza TMS. Jeśli import nadal zobaczy to zlecenie w TMS, pokaże rozbieżność.';

const who = (name: string | null) => (name ? `, ${name}` : '');

const IntakeCancellationBody = ({
  userOfferId,
  planningStatus,
}: {
  userOfferId: number;
  planningStatus?: unknown;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const canAct = useHasAnyPermission(['INTAKE_CS', 'INTAKE_ADMINISTRATION']);
  const [view, setView] = useState<IntakeCancellationView | null>(null);
  const [dialog, setDialog] = useState<'cancel' | 'deleted' | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await fetchIntakeCancellation(userOfferId));
    } catch {
      setView(null);
    }
  }, [userOfferId]);

  useEffect(() => {
    void load();
  }, [load, planningStatus]);

  const open = (kind: 'cancel' | 'deleted') => {
    setText('');
    setError(null);
    setDialog(kind);
  };

  const submit = async () => {
    if (dialog === 'cancel' && text.trim().length < MIN_CANCELLATION_REASON_LENGTH) {
      setError(`Podaj powód anulowania (co najmniej ${MIN_CANCELLATION_REASON_LENGTH} znaków)`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next =
        dialog === 'cancel'
          ? await cancelOrder(userOfferId, text.trim())
          : await markDeletedInTms(userOfferId, text);
      setView(next);
      setDialog(null);
      notify(dialog === 'cancel' ? 'Zlecenie anulowane w aplikacji' : 'Zapisano: usunięte w TMS', {
        type: 'success',
      });
      refresh();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!view) return null;

  const showCancel = canAct && cancellable(planningStatus, view);
  if (view.state === 'none' && !showCancel) return null;

  return (
    <Box data-testid="intake-cancellation" sx={{ mb: 2 }}>
      {view.state === 'none' ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => open('cancel')}
            data-testid="intake-cancel-button"
          >
            Anuluj zlecenie
          </Button>
        </Box>
      ) : (
        <Alert
          severity={view.state === 'awaiting_tms' ? 'warning' : 'info'}
          data-testid="intake-cancellation-state"
          action={
            view.state === 'awaiting_tms' && canAct ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => open('deleted')}
                data-testid="intake-deleted-in-tms-button"
              >
                Usunięte w TMS
              </Button>
            ) : undefined
          }
        >
          <AlertTitle>{view.stateLabel}</AlertTitle>
          {view.cancelled && (
            <Typography variant="body2">
              Anulowano {fmtDate(view.cancelled.at)}
              {who(view.cancelledByName)}. Powód: {view.cancelled.text ?? '—'}
            </Typography>
          )}
          {view.state === 'awaiting_tms' && (
            <Typography variant="body2">
              Usuń zlecenie w TMS, a potem oznacz je tutaj jako usunięte. Do TMS nic nie wysyłamy.
            </Typography>
          )}
          {view.deletedInTms && (
            <Typography variant="body2">
              Usunięte w TMS — oznaczono {fmtDate(view.deletedInTms.at)}
              {who(view.deletedInTmsByName)}
              {view.deletedInTms.text ? ` (${view.deletedInTms.text})` : ''}.
            </Typography>
          )}
        </Alert>
      )}
      {view.tmsStillActive && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="intake-cancellation-tms-still-active">
          Rozbieżność: TMS dalej podaje to zlecenie mimo oznaczenia „usunięte w TMS”
          {view.tmsStillActive.text ? ` — ${view.tmsStillActive.text}` : ''}. Sprawdź zlecenie w TMS.
        </Alert>
      )}
      <Dialog open={dialog !== null} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog === 'cancel' ? 'Anulować zlecenie?' : 'Usunięte w TMS'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {dialog === 'cancel' ? CANCEL_DIALOG_TEXT : DELETED_DIALOG_TEXT}
          </Typography>
          <TextField
            label={dialog === 'cancel' ? 'Powód anulowania' : 'Uwagi (opcjonalnie)'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            required={dialog === 'cancel'}
            slotProps={{
              htmlInput: {
                maxLength: 500,
                'data-testid': dialog === 'cancel' ? 'intake-cancel-reason' : 'intake-deleted-note',
              },
            }}
          />
          {error && (
            <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>
            Wróć
          </Button>
          <Button
            variant="contained"
            color={dialog === 'cancel' ? 'error' : 'primary'}
            onClick={() => void submit()}
            disabled={busy}
            data-testid="intake-cancel-confirm"
          >
            {dialog === 'cancel' ? 'Anuluj zlecenie' : 'Zapisz: usunięte w TMS'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Panel na karcie. Bez `userOfferId` bierze rekord karty zlecenia; karta
 * prezlecenia podaje wiersz banku (`PreOrder.userOfferId`).
 */
export const IntakeCancellationPanel = ({ userOfferId }: { userOfferId?: number | null }) => {
  const on = useFeature(FEATURE_INTAKE_CANCELLATION);
  const record = useRecordContext<RaRecord>();
  const id = userOfferId === undefined ? (record?.id as number | undefined) : userOfferId;
  if (!on || typeof id !== 'number') return null;
  return (
    <IntakeCancellationBody
      userOfferId={id}
      planningStatus={userOfferId === undefined ? record?.planningStatus : undefined}
    />
  );
};
